import type React from 'react';
import { Framework } from '@/framework';
import { normalizeStageToolbarConfig } from './config';
import { getDefaultHierarchyConfig, type HierarchyConfig } from './hierarchy';
import { getStageToolbarState } from './states/stage-toolbar-state';
import type { IStageToolbarItem, StageToolbarAction, StageToolbarConfigInput, StageToolbarItemKey } from './types';

/** 对外发布类型化工具栏业务动作的统一事件通道。 */
const ACTION_EVENT_NAME = 'stage-toolbar/action';
/** 通知现有业务当前活动面板 key 的轻量事件通道。 */
const PANEL_VISIBLE_EVENT_NAME = 'stage-toolbar/panel-visible-change';

/**
 * 舞台底部工具栏的实例 API 门面。
 *
 * 工具栏的可观察状态保存在按编辑器 `identity` 隔离的 Zustand store 中，当前实例只负责规范化输入、
 * 组织状态变更并通过框架事件服务发布业务动作。除查询方法和事件订阅外，修改方法均返回当前实例，
 * 便于编辑器初始化阶段链式配置。
 *
 * 图层菜单扩展配置包含函数和业务类型，因此由当前实例单独持有，不进入可序列化的视图状态。
 */
export class StageToolbar extends Framework {
    /** 当前编辑器实例的图层筛选、菜单和禁止隐藏类型扩展配置。 */
    private hierarchyConfig: HierarchyConfig;

    /**
     * 创建当前编辑器实例对应的工具栏控制器，并初始化默认图层菜单配置。
     *
     * @param identity - 编辑器实例的唯一标识，用于隔离不同编辑器的工具栏状态。
     */
    constructor(identity: string) {
        super(identity);
        this.hierarchyConfig = getDefaultHierarchyConfig(this.framework);
    }

    // #region 工具栏配置与条目扩展

    /**
     * 整体替换工具栏配置。
     *
     * 输入会先通过标准配置补齐缺省项；显式关闭的条目保持关闭。写入新配置时会同步关闭当前活动浮层，
     * 避免活动条目已被新配置移除后仍然显示。
     *
     * @param config - 工具栏布局和内置条目配置。
     * @returns 当前工具栏实例。
     */
    setConfig(config: StageToolbarConfigInput) {
        getStageToolbarState(this.identity).setConfig(normalizeStageToolbarConfig(config));
        return this;
    }

    /**
     * 获取当前已经归一化的完整工具栏配置。
     *
     * @returns 当前编辑器实例使用的工具栏配置。
     */
    getConfig() {
        return getStageToolbarState(this.identity).config;
    }

    /**
     * 整体替换工具栏条目集合。
     *
     * 条目 key 是扩展点的唯一身份：同一次设置中出现重复 key 时后写覆盖，便于业务替换内置实现；
     * 开发环境会提示重复 key 和非有限数值的 `sort`。`sort` 只负责未被配置 `itemOrder` 显式排序的条目。
     *
     * @param items - 新的完整条目集合。
     * @returns 当前工具栏实例。
     */
    setItems(items: IStageToolbarItem[]) {
        const itemMap = new Map<string, IStageToolbarItem>();
        items.forEach((item) => {
            if (itemMap.has(item.key) && process.env.NODE_ENV !== 'production') {
                console.warn(`[stage-toolbar] duplicate item key: ${item.key}`);
            }
            if (!Number.isFinite(item.sort) && process.env.NODE_ENV !== 'production') {
                console.warn(`[stage-toolbar] invalid item sort: ${item.key}`);
            }
            itemMap.set(item.key, item);
        });
        getStageToolbarState(this.identity).setItems([...itemMap.values()]);
        return this;
    }

    /**
     * 在现有条目集合后追加或覆盖条目。
     *
     * 新条目与已有条目 key 相同时复用 {@link setItems} 的后写覆盖规则。
     *
     * @param items - 需要追加或用于覆盖同 key 条目的集合。
     * @returns 当前工具栏实例。
     */
    addItems(items: IStageToolbarItem[]) {
        return this.setItems([...getStageToolbarState(this.identity).items, ...items]);
    }

    // #endregion

    // #region 容器显隐、条目可用性与活动浮层

    /**
     * 设置整个工具栏容器的运行时显隐状态，不修改静态条目配置。
     *
     * @param visible - `true` 显示工具栏，`false` 隐藏工具栏。
     * @returns 当前工具栏实例。
     */
    setVisible(visible: boolean) {
        getStageToolbarState(this.identity).setVisible(visible);
        return this;
    }

    /**
     * 显示整个工具栏容器。
     *
     * @returns 当前工具栏实例。
     */
    show() {
        return this.setVisible(true);
    }

    /**
     * 隐藏整个工具栏容器。
     *
     * @returns 当前工具栏实例。
     */
    hide() {
        return this.setVisible(false);
    }

    /**
     * 同步查询整个工具栏容器当前是否可见。
     *
     * @returns 工具栏当前的运行时显隐状态。
     */
    isVisible() {
        return getStageToolbarState(this.identity).visible;
    }

    /**
     * 设置单个条目的运行时可用性。
     *
     * 运行时可用性独立于静态配置；禁用当前活动条目时 store 会同步关闭其浮层。图层管理条目的变化还会
     * 发布 `hierarchy-availability-change` action，供外部入口同步禁用状态。
     *
     * @param key - 条目的唯一 key。
     * @param available - 条目是否可用。
     * @returns 当前工具栏实例。
     */
    setItemAvailability(key: StageToolbarItemKey, available: boolean) {
        getStageToolbarState(this.identity).setItemAvailability(key, available);
        if (key === 'hierarchy') {
            this.emitAction({ type: 'hierarchy-availability-change', available });
        }
        return this;
    }

    /**
     * 获取单个条目的运行时可用性。
     *
     * 未设置过运行时状态的条目默认可用；静态配置是否渲染仍由各条目自行判断。
     *
     * @param key - 条目的唯一 key。
     * @returns 条目当前是否可用。
     */
    getItemAvailability(key: StageToolbarItemKey) {
        return getStageToolbarState(this.identity).itemAvailability[key] ?? true;
    }

    /**
     * 设置当前唯一活动条目，用于保证工具栏浮层互斥。
     *
     * 这是不发布业务 action 的底层状态入口。控制图层面板时应优先使用 {@link setHierarchyVisible}，
     * 以便同时携带操作来源并通知外部消费者。
     *
     * @param key - 需要激活的条目 key；传入 `null` 关闭当前活动浮层。
     * @returns 当前工具栏实例。
     */
    setActiveItem(key: StageToolbarItemKey | null) {
        getStageToolbarState(this.identity).setActiveItem(key);
        return this;
    }

    // #endregion

    // #region 图层面板显隐

    /**
     * 显示或隐藏图层管理面板，并发布统一的显隐业务 action。
     *
     * `position` 是当前打开周期使用的临时位置覆盖，面板关闭后由工具栏视图清空；`source` 用于区分
     * 用户点击和外部 API 控制，未指定时按 API 操作处理。
     *
     * @param visible - `true` 打开图层面板，`false` 关闭图层面板。
     * @param options - 可选的面板位置和操作来源。
     * @returns 当前工具栏实例。
     */
    setHierarchyVisible(visible: boolean, options: { position?: React.CSSProperties; source?: 'user' | 'api' } = {}) {
        if (options.position) this.setHierarchyPanelPosition(options.position);
        this.setActiveItem(visible ? 'hierarchy' : null);
        this.emitAction({
            type: 'hierarchy-visible-change',
            visible,
            source: options.source || 'api',
        });
        return this;
    }

    /**
     * 根据当前活动条目切换图层管理面板显隐，并复用 {@link setHierarchyVisible} 的通知语义。
     *
     * @param options - 可选的面板位置和操作来源；未指定来源时按 API 操作处理。
     * @returns 当前工具栏实例。
     */
    toggleHierarchy(options: { position?: React.CSSProperties; source?: 'user' | 'api' } = {}) {
        const visible = getStageToolbarState(this.identity).activeItemKey !== 'hierarchy';
        return this.setHierarchyVisible(visible, options);
    }

    /**
     * 从外部业务入口切换图层管理面板。
     *
     * 该入口固定以 API 来源发布显隐 action，可选位置仅影响本次打开周期。
     *
     * @param position - 可选的图层面板临时定位样式。
     * @returns 当前工具栏实例。
     */
    hierarchyToggleShow(position?: React.CSSProperties) {
        return this.toggleHierarchy({ position, source: 'api' });
    }

    // #endregion

    // #region 自定义节点、参考线入口与工具栏布局

    /**
     * 设置由业务注入到工具栏的自定义 React 节点。
     *
     * 自定义节点是否渲染仍受 `items.customNode` 静态配置控制。
     *
     * @param node - 需要注入的 React 节点；传入 `null` 可清空节点。
     * @returns 当前工具栏实例。
     */
    addNode(node: React.ReactNode) {
        getStageToolbarState(this.identity).setCustomNode(node);
        return this;
    }

    /**
     * 获取当前业务注入的自定义 React 节点。
     *
     * @returns 当前自定义节点；未设置时为 `null`。
     */
    getNode() {
        return getStageToolbarState(this.identity).customNode;
    }

    /**
     * 设置参考线入口的运行时显隐状态，不修改参考线条目的静态配置。
     *
     * @param visible - 参考线入口是否可见。
     * @returns 当前工具栏实例。
     */
    setReferenceLineVisible(visible: boolean) {
        getStageToolbarState(this.identity).setReferenceLineVisible(visible);
        return this;
    }

    /**
     * 显示参考线入口。
     *
     * @returns 当前工具栏实例。
     */
    showReferenceLine() {
        return this.setReferenceLineVisible(true);
    }

    /**
     * 隐藏参考线入口。
     *
     * @returns 当前工具栏实例。
     */
    hideReferenceLine() {
        return this.setReferenceLineVisible(false);
    }

    /**
     * 同步查询参考线入口当前是否可见。
     *
     * @returns 参考线入口当前的运行时显隐状态。
     */
    isReferenceLineVisible() {
        return getStageToolbarState(this.identity).referenceLineVisible;
    }

    /**
     * 在右下角定位与水平居中定位之间切换工具栏。
     *
     * 该方法通过整体配置入口写入新位置，因此会沿用 {@link setConfig} 关闭活动浮层的副作用。
     *
     * @param center - `true` 使用居中定位，`false` 使用右下角定位。
     * @returns 当前工具栏实例。
     */
    setCenter(center: boolean) {
        const config = this.getConfig();
        return this.setConfig({
            ...config,
            position: center ? 'center' : 'right',
        });
    }

    /**
     * 查询工具栏当前是否使用水平居中定位。
     *
     * @returns 当前配置为居中定位时返回 `true`。
     */
    isCenter() {
        return this.getConfig().position === 'center';
    }

    // #endregion

    // #region 图层能力扩展与面板位置

    /**
     * 设置图层管理条目的运行时可用性。
     *
     * 这是 {@link setItemAvailability} 针对图层条目的语义化入口，会同步发布图层可用性 action。
     *
     * @param available - 图层管理条目是否可用。
     * @returns 当前工具栏实例。
     */
    setHierarchyAvailability(available: boolean) {
        return this.setItemAvailability('hierarchy', available);
    }

    /**
     * 获取图层管理条目的运行时可用性。
     *
     * @returns 图层管理条目当前是否可用。
     */
    getHierarchyAvailability() {
        return this.getItemAvailability('hierarchy');
    }

    /**
     * 获取当前实例持有的图层菜单扩展配置。
     *
     * 返回对象包含菜单回调等运行时能力；调用方应使用 {@link extendHierarchyConfig} 扩展配置，避免直接
     * 替换数组或破坏默认规则。
     *
     * @returns 当前图层菜单扩展配置。
     */
    getHierarchyConfig() {
        return this.hierarchyConfig;
    }

    /**
     * 增量扩展图层菜单配置。
     *
     * `onlyShowSortableLayers` 按传入值覆盖；`menuItems` 和 `preventHiddenTypes` 追加到已有默认规则，
     * 不执行去重。未提供的字段保持不变。
     *
     * @param config - 需要增量合并的图层菜单配置。
     * @returns 当前工具栏实例。
     */
    extendHierarchyConfig(config: Partial<HierarchyConfig>) {
        const { onlyShowSortableLayers, menuItems, preventHiddenTypes } = config;

        if (onlyShowSortableLayers !== undefined) {
            this.hierarchyConfig.onlyShowSortableLayers = onlyShowSortableLayers;
        }
        if (menuItems?.length) {
            this.hierarchyConfig.menuItems = [...this.hierarchyConfig.menuItems, ...menuItems];
        }
        if (preventHiddenTypes) {
            this.hierarchyConfig.preventHiddenTypes = [
                ...this.hierarchyConfig.preventHiddenTypes,
                ...preventHiddenTypes,
            ];
        }
        return this;
    }

    /**
     * 获取图层面板当前的临时定位样式。
     *
     * @returns 当前面板位置覆盖；未设置或关闭面板后为空对象。
     */
    getHierarchyPanelPosition() {
        return getStageToolbarState(this.identity).hierarchyPanelPosition;
    }

    /**
     * 整体替换图层面板的临时定位样式。
     *
     * 工具栏视图会在图层面板不再活动时清空该状态，防止后续用户打开时继承旧位置。
     *
     * @param position - React 定位样式；传入空对象可立即清除位置覆盖。
     * @returns 当前工具栏实例。
     */
    setHierarchyPanelPosition(position: React.CSSProperties) {
        getStageToolbarState(this.identity).setHierarchyPanelPosition(position);
        return this;
    }

    // #endregion

    // #region 业务事件订阅与发布

    /**
     * 订阅工具栏活动面板类型变化。
     *
     * 这是面向现有业务的轻量面板通知，回调参数为空字符串表示没有活动面板；需要图层操作、缩放等
     * 类型化业务动作时应使用 {@link onAction}。
     *
     * @param callback - 面板类型变化回调。
     * @returns 取消本次订阅的函数。
     */
    onPanelVisible(callback: (type: string) => void) {
        const handler = this.framework.service.events.on(PANEL_VISIBLE_EVENT_NAME, callback);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 发布当前活动面板类型。
     *
     * 该方法供工具栏视图在 `activeItemKey` 变化后同步业务监听方；空字符串表示所有浮层均已关闭。
     *
     * @param type - 当前活动面板 key，或表示无活动面板的空字符串。
     * @returns 当前工具栏实例。
     */
    emitPanelVisibleChange(type: string) {
        this.framework.service.events.emit(PANEL_VISIBLE_EVENT_NAME, type);
        return this;
    }

    /**
     * 订阅工具栏产生的公开类型化业务动作。
     *
     * action 联合类型覆盖图层显隐与操作、条目可用性、缩放和适配视口；内部图层事件名不会通过该接口
     * 暴露给消费者。
     *
     * @param callback - 接收工具栏业务 action 的回调。
     * @returns 取消本次订阅的函数。
     */
    onAction(callback: (action: StageToolbarAction) => void) {
        const handler = this.framework.service.events.on(ACTION_EVENT_NAME, callback);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 发布一个公开类型化工具栏业务动作。
     *
     * 该方法主要供内置条目和内部事件桥接组件使用；只负责事件分发，不修改工具栏状态。
     *
     * @param action - 需要发布的工具栏业务 action。
     * @returns 当前工具栏实例。
     */
    emitAction(action: StageToolbarAction) {
        this.framework.service.events.emit(ACTION_EVENT_NAME, action);
        return this;
    }

    // #endregion
}
